import {
  Box,
  Building2,
  Car,
  DollarSign,
  Handshake,
  Lightbulb,
  type LucideIcon,
  Megaphone,
  MoreHorizontal,
  Receipt,
  Scale,
  ShieldCheck,
  SprayCan,
  Tag,
  Users,
  Wrench,
} from "lucide-react";
import type { TaxAdvantageTone } from "@/hooks/use-accounting";

/**
 * Money, e.g. `$12,400` / `-$3,200`. Whole values stay clean; values with a
 * fractional part show 2 decimals (`$12,399.60` / `-$3,200.25`).
 */
export function fmtMoney(n: number): string {
  const v = Math.round((n || 0) * 100) / 100;
  const digits = Number.isInteger(v) ? 0 : 2;
  return (
    (v < 0 ? "-" : "") +
    "$" +
    Math.abs(v).toLocaleString(undefined, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    })
  );
}

/** Compact money for chart axes/labels, e.g. `$12k`. */
export function fmtMoneyShort(n: number): string {
  const v = Math.round(n || 0);
  const sign = v < 0 ? "-" : "";
  const a = Math.abs(v);
  if (a >= 1000) {
    const k = a / 1000;
    return sign + "$" + (k >= 10 ? k.toFixed(0) : k.toFixed(1)) + "k";
  }
  return sign + "$" + a;
}

/** Tailwind classes for the soft "pill" used by tax-advantage tags/group headers. */
export const TONE_PILL: Record<TaxAdvantageTone, string> = {
  green: "bg-success/15 text-success border-success/25",
  blue: "bg-blue-500/15 text-blue-500 border-blue-500/25",
  amber: "bg-warning/15 text-warning border-warning/25",
  red: "bg-destructive/15 text-destructive border-destructive/25",
};

/** Solid accent color for the small square marker beside each tax-advantage item. */
export const TONE_MARKER: Record<TaxAdvantageTone, string> = {
  green: "bg-success/60",
  blue: "bg-blue-500/60",
  amber: "bg-warning/60",
  red: "bg-destructive/60",
};

/** Icon shown beside each Schedule E expense category in the Top deductions list. */
export const CATEGORY_ICONS: Record<string, LucideIcon> = {
  depreciation: Building2,
  mortgage_interest: DollarSign,
  taxes: Receipt,
  insurance: ShieldCheck,
  management_fees: Users,
  cleaning_maintenance: SprayCan,
  repairs: Wrench,
  utilities: Lightbulb,
  advertising: Megaphone,
  auto_travel: Car,
  supplies: Box,
  commissions: Handshake,
  legal_professional: Scale,
  other_interest: DollarSign,
  other: MoreHorizontal,
};

export function categoryIcon(key: string): LucideIcon {
  return CATEGORY_ICONS[key] ?? Tag;
}
